import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import CommercialNotice from "../components/CommercialNotice";
import OutreachForm from "../components/outreach/OutreachForm";
import OutreachBlock from "../components/outreach/OutreachBlock";
import OutreachHistory from "../components/outreach/OutreachHistory";
import { Sparkles, RefreshCw, Send, AlertCircle, FlaskConical } from "lucide-react";

export default function Outreach() {
  const [searchParams] = useSearchParams();
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [channel, setChannel] = useState("email");
  const [tone, setTone] = useState("professional");
  const [goal, setGoal] = useState("product_sponsorship");
  const [out, setOut] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState("");
  const [sendErr, setSendErr] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [sends, setSends] = useState([]);
  const [limitErr, setLimitErr] = useState("");

  const loadSends = () =>
    api.get("/outreach/sends").then(({ data }) => setSends(data)).catch(() => {});

  const sendTestToSelf = async () => {
    if (!out || !brandId) return;
    setTestSending(true); setSendMsg(""); setSendErr(false);
    try {
      const { data } = await api.post("/outreach/send", {
        brand_id: brandId, channel, subject: out.subject || "", message: out.message, test_send: true,
      });
      if (data.status === "sent") {
        setSendMsg(`Test preview sent to ${data.recipient}. Check your inbox to see exactly what the brand will receive — this didn't count against your quota and didn't move the deal.`);
      } else {
        setSendErr(true);
        setSendMsg(data.error || `Test preview status: ${data.status}`);
      }
    } catch (e) {
      setSendErr(true);
      setSendMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setTestSending(false); }
  };

  const send = async () => {
    if (!out || !brandId) return;
    setSending(true); setSendMsg(""); setSendErr(false);
    try {
      const { data } = await api.post("/outreach/send", {
        brand_id: brandId, channel, subject: out.subject || "", message: out.message,
      });
      setSendMsg(
        data.configured
          ? `Sent via Resend as ${data.from_header} — replies route to ${data.reply_to}.`
          : `Logged as ${data.status}. Email delivery is stubbed — add RESEND_API_KEY to actually send. Brand will see From: ${data.from_header || "PITCHD"} · Reply-To: ${data.reply_to || "your email"}. Deal moved to Contacted.`
      );
      loadSends();
    } catch (e) {
      setSendErr(true);
      setSendMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };

  useEffect(() => {
    api.get("/brands").then(({ data }) => {
      setBrands(data);
      // Pre-select from ?brand=ID query param (from Sponsor Finder handoff), else first brand
      const fromQuery = searchParams.get("brand");
      if (fromQuery && data.find(b => b.id === fromQuery)) setBrandId(fromQuery);
      else if (data[0]) setBrandId(data[0].id);
    });
    loadSends();
  }, [searchParams]);

  const generate = async () => {
    if (!brandId) return;
    setLoading(true); setError(""); setOut(null); setLimitErr("");
    try {
      const { data } = await api.post("/ai/outreach", { brand_id: brandId, channel, tone, goal });
      setOut(data);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      if (e.response?.status === 402) setLimitErr(msg); else setError(msg);
    } finally { setLoading(false); }
  };

  const copy = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-12">
        <div className="mb-10">
          <div className="label-eyebrow">AI generator</div>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="outreach-title">Outreach</h1>
          <p className="text-ink-muted mt-2 text-sm">Personalized brand pitches in seconds.</p>
        </div>

        <div className="grid md:grid-cols-12 gap-6">
          <OutreachForm
            brands={brands} brandId={brandId} setBrandId={setBrandId}
            channel={channel} setChannel={setChannel}
            tone={tone} setTone={setTone}
            goal={goal} setGoal={setGoal}
            loading={loading} onGenerate={generate}
          />

          <div className="md:col-span-7 space-y-3">
            {limitErr && (
              <div className="surface p-5 border-volt/40 flex items-start gap-3" data-testid="outreach-limit">
                <AlertCircle className="w-5 h-5 text-volt mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-heading text-base uppercase tracking-tight">Plan limit reached</div>
                  <div className="text-sm text-ink-muted mt-1">{limitErr}</div>
                  <Link to="/app/pricing" className="btn-volt inline-block mt-3" data-testid="outreach-upgrade-link">Upgrade plan</Link>
                </div>
              </div>
            )}
            {error && <div className="surface p-4 border-[#FF3B30]/40 text-sm text-[#FF3B30]" data-testid="outreach-error">{error}</div>}
            {!out && !error && (
              <div className="surface p-12 text-center">
                <Sparkles className="w-8 h-8 text-volt mx-auto mb-4" />
                <h2 className="font-heading text-2xl uppercase">Pick a brand and generate</h2>
                <p className="text-ink-muted mt-2 text-sm">Your personalized outreach will appear here.</p>
              </div>
            )}
            {out && (
              <div className="space-y-3" data-testid="outreach-output">
                {out.subject && (
                  <OutreachBlock label="Subject" text={out.subject} onCopy={() => copy("subject", out.subject)} copied={copied === "subject"} />
                )}
                <OutreachBlock label="Message" text={out.message} onCopy={() => copy("message", out.message)} copied={copied === "message"} big />
                <OutreachBlock label="Follow-up (3 days later)" text={out.followup} onCopy={() => copy("followup", out.followup)} copied={copied === "followup"} />
                <OutreachBlock label="Pitch angle" text={out.pitch_angle} onCopy={() => copy("pitch", out.pitch_angle)} copied={copied === "pitch"} />
                <div className="surface p-5 flex flex-wrap items-center gap-3">
                  {channel === "email" && (
                    <button
                      onClick={sendTestToSelf}
                      disabled={testSending || sending}
                      className="btn-ghost-line inline-flex items-center gap-2 disabled:opacity-50"
                      data-testid="outreach-test-send-btn"
                      title="Sends a preview to your own email so you can see exactly what the brand will receive. Doesn't count against your quota."
                    >
                      {testSending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
                      {testSending ? "Sending…" : "Send test to me"}
                    </button>
                  )}
                  <button onClick={send} disabled={sending || testSending} className="btn-volt inline-flex items-center gap-2 disabled:opacity-50" data-testid="outreach-send-btn">
                    {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {sending ? "Sending…" : "Send via PITCHD"}
                  </button>
                  {sendMsg && <div className={`text-xs ${sendErr ? "text-[#FF3B30]" : "text-ink-muted"}`} data-testid="send-msg">{sendMsg}</div>}
                </div>
              </div>
            )}
          </div>
        </div>

        <OutreachHistory sends={sends} onRefresh={loadSends} />

        <CommercialNotice />
      </div>
    </DashboardLayout>
  );
}
